import { haversineKm } from "../../src/distance";
import type { Stop, TravelMode, TripState } from "../../src/types";

/** Parse a strict ISO date (YYYY-MM-DD) into a local Date, or null. Mirrors summary.ts. */
export function parseISODate(value: string | undefined): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Whole days from `a` to `b` (b - a), ignoring time-of-day. */
export function daysBetween(a: Date, b: Date): number {
  const ms = startOfDay(b).getTime() - startOfDay(a).getTime();
  return Math.round(ms / 86_400_000);
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Earliest dateFrom and latest dateTo across all stops, or null if the trip has no dates. */
export function tripDateSpan(trip: TripState): { start: Date; end: Date } | null {
  let start: Date | null = null;
  let end: Date | null = null;
  for (const stop of trip.stops) {
    const from = parseISODate(stop.dateFrom);
    const to = parseISODate(stop.dateTo) ?? from;
    const lo = from ?? to;
    const hi = to ?? from;
    if (lo && (!start || lo < start)) start = lo;
    if (hi && (!end || hi > end)) end = hi;
  }
  return start && end ? { start, end } : null;
}

export type TripStats = {
  name: string;
  createdAt: string;
  durationDays: number | null;
  stopCount: number;
  hotelCount: number;
  totalKm: number;
  modes: TravelMode[];
  cities: string[];
};

export function tripStats(trip: TripState): TripStats {
  const span = tripDateSpan(trip);
  const durationDays = span ? daysBetween(span.start, span.end) + 1 : null;

  let totalKm = 0;
  for (let i = 0; i < trip.stops.length - 1; i++) {
    totalKm += haversineKm(trip.stops[i], trip.stops[i + 1]);
  }

  const modeSet = new Set<TravelMode>();
  for (const stop of trip.stops) {
    if (stop.arriveBy) modeSet.add(stop.arriveBy);
    if (stop.departBy) modeSet.add(stop.departBy);
  }

  const hotelCount = trip.stops.filter((s) => hasHotel(s)).length;
  const cities = trip.stops.map((s) => s.city || s.name).filter(Boolean);

  return {
    name: trip.tripName?.trim() || "(sin nombre)",
    createdAt: trip.createdAt,
    durationDays,
    stopCount: trip.stops.length,
    hotelCount,
    totalKm: Math.round(totalKm),
    modes: [...modeSet],
    cities,
  };
}

function hasHotel(stop: Stop): boolean {
  const h = stop.hotel;
  return !!h && (!!h.name || !!h.address || h.lat != null || h.checkIn != null);
}

export type DayCell = {
  dayIndex: number;
  city: string;
  isTravelDay: boolean;
  arriveBy?: TravelMode;
  placesCount: number;
  /** True when this timeline was derived from stop order because the trip has no dates. */
  approx: boolean;
};

/**
 * Day-by-day timeline aligned to a *relative* day number (Day 1 = the trip's start).
 * With dates: one cell per calendar day between the earliest dateFrom and latest dateTo,
 * resolving which stop is active. Without dates: one pseudo-day per stop (approx).
 */
export function buildDayTimeline(trip: TripState): DayCell[] {
  const span = tripDateSpan(trip);
  if (!span) return fallbackTimeline(trip);

  const cells: DayCell[] = [];
  const total = daysBetween(span.start, span.end) + 1;
  let prevStopId: string | null = null;

  for (let i = 0; i < total; i++) {
    const day = new Date(span.start.getFullYear(), span.start.getMonth(), span.start.getDate() + i);
    const stop = activeStopOn(trip.stops, day);
    const isTravelDay = !!stop && stop.id !== prevStopId;
    cells.push({
      dayIndex: i + 1,
      city: stop ? stop.city || stop.name : "—",
      isTravelDay,
      arriveBy: isTravelDay ? stop?.arriveBy : undefined,
      placesCount: stop?.places?.length ?? 0,
      approx: false,
    });
    if (stop) prevStopId = stop.id;
  }
  return cells;
}

function activeStopOn(stops: readonly Stop[], day: Date): Stop | null {
  for (const stop of stops) {
    const from = parseISODate(stop.dateFrom);
    const to = parseISODate(stop.dateTo) ?? from;
    if (from && to && day >= startOfDay(from) && day <= startOfDay(to)) return stop;
  }
  return null;
}

function fallbackTimeline(trip: TripState): DayCell[] {
  return trip.stops.map((stop, i) => ({
    dayIndex: i + 1,
    city: stop.city || stop.name || "—",
    isTravelDay: i > 0,
    arriveBy: stop.arriveBy,
    placesCount: stop.places?.length ?? 0,
    approx: true,
  }));
}
